import React, { useState } from 'react';
import { useMutation } from 'react-query';
import { addTemplatesToAssessment } from 'src/api/scenarioTemplates';
import { showErrorToast } from 'src/components/Toast';
import useVariables from 'src/hooks/useVariables/useVariables';
import { getErrorMessage } from 'src/utils/error';
import { commitAfterReview } from './commitAfterReview';
import { MissingVariablesModal } from './MissingVariablesModal';
import { useSaveMissingVariables } from './useSaveMissingVariables';
import { buildMissingVariableEntries, MissingVariableEntry } from './util';

interface UseAddScenariosWithVariableReviewConfig {
  /** Target assessment. Empty until the user picks one on the library surfaces. */
  assessmentId: string;
  /** Per-site completion — the call sites genuinely differ (panel reset, toasts, refetch, close). */
  afterSuccess: (templateIds: string[]) => void | Promise<void>;
  /**
   * Failure sink. Omit where the host surface has no error line of its own — the fallback
   * toast paints above the review modal's overlay, which an inline line would sit under.
   */
  onError?: (err: unknown) => void;
  /** Runs immediately before every add attempt, retries from inside the review included. */
  onBeforeAttempt?: () => void;
}

interface ReviewState {
  entries: MissingVariableEntry[];
  plural: boolean;
  commit: (fromReview: boolean) => Promise<void>;
}

/**
 * The Missing-Variables review (nudge) for adding scenarios FROM THE LIBRARY (CRPD-7308).
 *
 * Detection is front-end: the selected templates' consequence formulas are checked against
 * the merged assessment+workspace variables, and any token that is unresolved or resolves to
 * a valueless variable opens `MissingVariablesModal` (`destination: 'assessment'`) before the
 * add. Save & Proceed populates them first, Skip adds anyway — the nudge NEVER blocks.
 * Ordering is `commitAfterReview`'s (CRPD-7287): the review stays on screen until the add
 * resolves, and stays on screen if it throws, so the user's selection survives a retry.
 *
 * The consumer renders `reviewModal` in its tree, folds `isChecking` into its add button's
 * `disabled` (see the note on `isChecking` below) and `isAdding` into its `loading`.
 *
 * This is the scenario-side twin of `useControlCostVariableReview`, which detects via a BE
 * query instead; there is no scenario equivalent of that query.
 */
export const useAddScenariosWithVariableReview = ({
  assessmentId,
  afterSuccess,
  onError,
  onBeforeAttempt,
}: UseAddScenariosWithVariableReviewConfig) => {
  const scopedAssessmentId = assessmentId || undefined;
  const { variables, isLoading: isChecking } = useVariables({ assessmentId: scopedAssessmentId });
  const saveMissingVariables = useSaveMissingVariables({ assessmentId: scopedAssessmentId });
  const [add] = useMutation(addTemplatesToAssessment);

  const [review, setReview] = useState<ReviewState | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * `fromReview` decides whether the failure ALSO needs a toast. The host surfaces show their
   * own inline error line, but that line sits UNDER the review modal's overlay — so a failure
   * taken from inside the review would be invisible without one (the CRPD-7287 reason). On the
   * straight-through path the inline line is visible, so a toast would double-report.
   */
  const handleError = (err: unknown, { fromReview }: { fromReview: boolean }) => {
    onError?.(err);
    if (fromReview || !onError) showErrorToast(getErrorMessage(err, 'Something went wrong'));
  };

  const doAdd = async (templateIds: string[], fromReview: boolean) => {
    setIsAdding(true);
    try {
      // `throwOnError` is load-bearing: react-query v2 RESOLVES with undefined on failure
      // otherwise, and `commitAfterReview` needs the commit to throw to keep the review open
      // (and to stop `afterSuccess` reporting a success that never happened).
      await add({ assessments: [assessmentId], templates: templateIds }, { throwOnError: true });
    } finally {
      setIsAdding(false);
    }

    // PAST THIS POINT THE ADD HAS COMMITTED SERVER-SIDE. `afterSuccess` refetches and closes,
    // and its failure must NOT propagate: `commitAfterReview` would keep the review open and a
    // retry would add the same templates a second time. Report it, never rethrow it.
    try {
      await afterSuccess(templateIds);
    } catch (err) {
      handleError(err, { fromReview });
    }
  };

  const requestAdd = async (templateIds: string[], formulas: (string | number | null | undefined)[]) => {
    // Clear the host's stale inline error up front, so a previous attempt's message is not left
    // rendered underneath the review (and re-exposed when the review is dismissed).
    onBeforeAttempt?.();

    const commit = async (fromReview: boolean) => {
      // …and again before every retry taken from inside the review.
      onBeforeAttempt?.();
      await doAdd(templateIds, fromReview);
    };

    // Detection needs a real, successfully-loaded variable list. Two traps here:
    //  - `useVariables` publishes a NON-NULL EMPTY array when the workspace query fails
    //    (Apollo `errorPolicy: 'none'` → `data` undefined → `|| []`) with `isLoading` already
    //    false. Every token would then resolve to nothing and be reported "missing", and
    //    Save & Proceed would create variables named after raw ids that do not populate the
    //    formula. A real workspace always carries the global variables, so an empty list means
    //    "unavailable", not "none" — skip detection and add, rather than prompt with garbage.
    //  - `useVariables` keeps its PREVIOUS list while a newly picked assessment's variables
    //    load, so this guard cannot cover that window either; `isChecking` on the add button is
    //    what does. Detection never blocks the add (see the hook doc).
    if (variables?.length) {
      const entries = buildMissingVariableEntries(formulas, variables);
      if (entries.length) {
        setReview({ entries, plural: templateIds.length > 1, commit });
        return;
      }
    }

    try {
      await commit(false);
    } catch (err) {
      handleError(err, { fromReview: false });
    }
  };

  const reviewModal = review ? (
    <MissingVariablesModal
      entries={review.entries}
      destination="assessment"
      plural={review.plural}
      saving={isSaving}
      onSaveAndProceed={async (values) =>
        commitAfterReview({
          save: () => saveMissingVariables(values),
          commit: () => review.commit(true),
          closeReview: () => setReview(null),
          onError: (err) => handleError(err, { fromReview: true }),
          setSaving: setIsSaving,
        })
      }
      onSkip={async () =>
        commitAfterReview({
          commit: () => review.commit(true),
          closeReview: () => setReview(null),
          onError: (err) => handleError(err, { fromReview: true }),
          setSaving: setIsSaving,
        })
      }
      onClose={() => setReview(null)}
    />
  ) : null;

  return { requestAdd, reviewModal, isAdding: isAdding || isSaving || isChecking, isChecking };
};
